import { Outlet, useOutletContext } from 'react-router-dom'

import { TaskPageDocument } from 'generated/resources/graphql'

import type { Employee } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { canAccessWelcomeSpace } from 'modules/employees/lib/utils'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import UpsellProjectsInTasksModal from 'modules/project_management/components/modals/UpsellProjectsInTasksModal'
import TasksList from 'modules/tasks/components/TasksList'
import { TaskAnalytics } from 'modules/tasks/components/TasksList/components/TaskAnalytics'
import { analyticsVariables } from 'modules/tasks/components/TasksList/components/TaskAnalytics/analyticsData'
import { TaskIdentifier } from 'modules/tasks/lib/task'

const TasksPage = () => {
  const { currentUserId, loading, error, employee } = useOutletContext<{
    error: unknown
    currentUserId: number
    loading: boolean
    employee: Employee
  }>()
  const { data, loading: loadingTaskPageData } = useQuery(TaskPageDocument, {
    variables: analyticsVariables(currentUserId),
  })
  const hideActions = canAccessWelcomeSpace(employee)

  useEffect(() => {
    track(trackingEvents.projectManagement.tasks.employeeTasksVisits)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { dueInfuture, overdue, noDueDate, total } = data?.tasks || {}
  const totalOverDue = overdue?.totalCount || 0
  const totalDueInfuture = dueInfuture?.totalCount || 0
  const noDueDateTotal = noDueDate?.totalCount || 0
  const totalTasks = total?.totalCount || 0
  return (
    <ResponsivePage.Body
      loading={loading || loadingTaskPageData}
      error={error}
      variant="wide"
      header={{
        widget: (
          <TaskAnalytics
            data={{
              total: totalTasks,
              totalDueInfuture,
              totalOverDue,
              noDueDateTotal,
            }}
            title={i18n.t('tasks.analytics.total_task')}
          />
        ),
      }}
    >
      <UpsellProjectsInTasksModal />
      <TasksList
        identifier={TaskIdentifier.Single}
        hideActions={hideActions}
        isExternalUser={!employee}
      />
      {/* tasks list should fetch display only single task(task assigned to or created by the current user) */}
      <Outlet context={{ currentUserId }} />
    </ResponsivePage.Body>
  )
}

export default TasksPage
